/**
 * Reduce a browser path to its route shape before it is transmitted.
 *
 * Some routes carry a single-use token as a path segment — password reset and
 * email verification. Crash reports and page views both send the current path,
 * so a crash on one of those screens shipped a live token to the server and
 * into an admin-readable table.
 *
 * The server redacts again on arrival. This exists so the value never leaves the
 * browser in the first place; a single point of failure either side is not
 * enough for something that carries credentials.
 */

const PATH_RULES = [
  [/^\/reset-password\/[^/]+/, "/reset-password/:token"],
  [/^\/verify-email\/[^/]+/, "/verify-email/:token"],
  [/^\/auth\/callback/, "/auth/callback"],
  // A public trade-list slug is a bearer link to someone's list, and at 12
  // characters too short for ID_SEGMENT (docs/PUBLIC_TRADE_LIST.md).
  [/^\/t\/[^/]+/i, "/t/:slug"],
  [/^\/trade-list\/[^/]+/i, "/trade-list/:slug"],
];

// Mongo-style ids and long opaque strings are identifiers, not route shape.
const ID_SEGMENT = /^[a-f0-9]{24}$|^[A-Za-z0-9_-]{20,}$/;

export const redactPath = (rawPath) => {
  if (typeof rawPath !== "string" || rawPath.length === 0) return "";

  // Query and fragment go unconditionally: recovery tokens arrive in the hash
  // on some providers, and search terms arrive in the query.
  const path = rawPath.split("?")[0].split("#")[0];

  for (const [pattern, replacement] of PATH_RULES) {
    if (pattern.test(path)) return replacement;
  }

  return path
    .split("/")
    .map((segment) => (ID_SEGMENT.test(segment) ? ":id" : segment))
    .join("/")
    .slice(0, 256);
};

/** Current location, redacted. Safe to call outside a browser. */
export const currentRedactedPath = () => {
  if (typeof window === "undefined" || !window.location) return "";
  return redactPath(window.location.pathname);
};

export default redactPath;
